import SearchIcon from '@mui/icons-material/Search';
import { CardHeader, Grid, IconButton } from '@mui/material';
import moment from 'moment';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { removeCpfFormatting } from 'utils/formatters';
import { PeriodFilterValuesEnum } from 'utils/periodEnum';
import { useStoreSelector } from 'v3/data/store';
import { ContractingCancellationStateEnum } from 'v3/data/store/cancellationNavigation/enum';
import { CancellationStateMapping } from 'v3/data/store/cancellationNavigation/mapping';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { clearFilters, setFilters, setOnlineContractingList } from 'v3/data/store/onlineContracting/actions';
import { OnlineContractingStateEnum } from 'v3/data/store/onlineContracting/enums';
import { filtersInitialState } from 'v3/data/store/onlineContracting/state';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import * as api from 'v3/services/online/api';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import { MiniButton } from 'v3/shared/components/Button/MiniButton';
import { OnlineContractingStatusMap } from 'v3/shared/components/Filters/OnlineFilter/enum';
import { filterRequestFactory, getDateRange } from 'v3/shared/components/Filters/OnlineFilter/factory';
import { CustomFilterType } from 'v3/shared/components/Filters/OnlineFilter/types';
import { DatePickerFormController } from 'v3/shared/components/Inputs/DatePickerFormController';
import { DropdownSelectFormController } from 'v3/shared/components/Inputs/DropdownSelectFormController';
import { InputFormController } from 'v3/shared/components/Inputs/InputFormController';
import { TextMaskCustom } from 'v3/shared/components/Inputs/MaskCustomInput';
import { BodyLarge } from 'v3/shared/typography/BodyLarge';
import { AlignButtons, CardContentMain, CardContentStyled, CardStyled, DownIcon, GridStyled, UpIcon } from './styles';

const statusOptions = Object.values(OnlineContractingStateEnum).map((status) => ({
  value: status,
  label: OnlineContractingStatusMap[status],
}));

const cancellationStatusOptions = Object.values(ContractingCancellationStateEnum).map((status) => ({
  value: status,
  label: CancellationStateMapping[status].title,
}));

export const OnlineFilters = () => {
  const [expanded, setExpanded] = useState(false);
  const { isTablet } = useBreakpoints();
  const { filter } = useStoreSelector((store) => store.onlineContracting);
  const { rowsPerPage, page, ...customFilters } = filter;
  const [datesDisabled, setDatesDisabled] = useState(false);

  const { control, getValues, reset } = useForm<CustomFilterType>({
    mode: 'onChange',
    values: customFilters,
    reValidateMode: 'onChange',
  });

  const getContractingList = useCallback(
    async (isResetFilter?: boolean) => {
      const filterValues = getValues();
      const filtersFormated = isResetFilter ? {} : filterRequestFactory(filterValues, filter);

      try {
        const response = await api.getContractingListInApp({
          ...filtersFormated,
          rowsPerPage,
          page,
        });
        setOnlineContractingList(response.data);
      } catch (error) {
        showErrorDialog('Erro ao obter lista de contratações', error);
      }
    },
    [page, rowsPerPage],
  );

  useEffect(() => {
    getContractingList();
  }, [getContractingList]);

  const handlePeriodChange = (value: keyof typeof PeriodFilterValuesEnum) => {
    if (!value?.length) {
      setDatesDisabled(false);
      setFilters({ ...filter, startDate: '', finalDate: '', period: value });
    } else {
      setDatesDisabled(true);
      const { startDate, finalDate } = getDateRange(value);
      setFilters({ ...filter, startDate, finalDate, period: value });
    }
  };

  const handleClear = async () => {
    clearFilters();
    reset(filtersInitialState);
    setExpanded(false);
    setDatesDisabled(false);
    getContractingList(true);
  };

  const startDate = useWatch({
    control,
    name: 'startDate',
  });

  const finalDate = useWatch({
    control,
    name: 'finalDate',
  });

  return (
    <CardStyled>
      <CardHeader title={<BodyLarge>Filtro</BodyLarge>} />
      <CardContentStyled>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 3 }} component="div">
            <InputFormController
              data-testid="input-cpf"
              inputType="tel"
              formControl={control}
              formControlName="cpf"
              label="Procurar por CPF"
              mask="000.000.000-00"
              placeholder="___.___.___-__"
              customMask={TextMaskCustom}
              fullWidth
              startAdornmentIcon={SearchIcon}
              onChangeInput={(cpf) => setFilters({ ...filter, cpf: removeCpfFormatting(cpf) })}
            />
          </Grid>

          <GridStyled size={{ xs: 12, sm: 6, md: 3, lg: 2, xl: 3 }}>
            <DropdownSelectFormController
              data-testid="select-form-controller-cancellation-status"
              formControl={control}
              fullWidth
              formControlName="cancellationStatus"
              label="Status cancelamento"
              selectValues={cancellationStatusOptions}
              onChangeSelect={(cancellationStatus) => setFilters({ ...filter, cancellationStatus })}
            />
          </GridStyled>

          <GridStyled size={{ xs: 12, sm: 6, md: 3, lg: 2, xl: 3 }}>
            <DropdownSelectFormController
              data-testid="select-form-controller-status"
              formControl={control}
              fullWidth
              formControlName="status"
              label="Status financiamento"
              selectValues={statusOptions}
              onChangeSelect={(status) => setFilters({ ...filter, status })}
            />
          </GridStyled>

          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 3 }} order={{ xs: 99, sm: 'unset' }} component="div">
            <AlignButtons>
              <IconButton onClick={() => setExpanded(!expanded)}>{expanded ? <UpIcon /> : <DownIcon />}</IconButton>
              {isTablet ? (
                <MiniButton icon={SearchIcon} handleClick={getContractingList} />
              ) : (
                <FlexButton text="Pesquisar" size="small" handleClick={getContractingList} />
              )}

              <MiniButton text="Limpar" $backgroundColor="transparent" handleClick={handleClear} />
            </AlignButtons>
          </Grid>

          <Grid size={{ xs: 12 }} component="div">
            {expanded ? (
              <CardContentMain>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4, md: 4, lg: 3 }} component="div">
                    <DropdownSelectFormController
                      data-testid="select-form-controller-period"
                      formControl={control}
                      formControlName="period"
                      label="Período"
                      selectValues={PeriodFilterValuesEnum}
                      enumType
                      onChangeSelect={handlePeriodChange}
                    />
                  </Grid>

                  {!datesDisabled ? (
                    <Fragment>
                      <Grid size={{ xs: 12, sm: 4, md: 4, lg: 3 }} component="div">
                        <DatePickerFormController
                          formControl={control}
                          formControlName="startDate"
                          label="Início"
                          defaultValue={filter.startDate ? moment(filter.startDate) : null}
                          onChangeDatePicker={(startDate) => setFilters({ ...filter, startDate })}
                          disableFuture
                          maxDate={finalDate ? moment(finalDate) : undefined}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 4, md: 4, lg: 3 }} component="div">
                        <DatePickerFormController
                          formControl={control}
                          formControlName="finalDate"
                          label="Fim"
                          defaultValue={filter.finalDate ? moment(filter.finalDate) : null}
                          onChangeDatePicker={(finalDate) => setFilters({ ...filter, finalDate })}
                          disableFuture
                          minDate={startDate ? moment(startDate) : undefined}
                        />
                      </Grid>
                    </Fragment>
                  ) : null}
                </Grid>
              </CardContentMain>
            ) : null}
          </Grid>
        </Grid>
      </CardContentStyled>
    </CardStyled>
  );
};
